import type { PaceRefusalCode } from '@norum/contracts';
import type { WireSequence, WireLiftRequest } from '@/api/hooks';
import type {
  WireCampaign,
  WireLifecycleContact,
  WireMailing,
  WireMailingRequest,
  WireMarketDay,
  WireOutreachDigest,
  WireProspect,
  WireQueuedTouch,
  WireSuppressionCounts,
} from '@/api/wire';
import type { ItemContext, Party, StepAction } from '@/ui/context';
import { when } from '@/ui/records';
import type { Who } from '@/ui/who';
import { countryName, countryNames, languageName } from '@/ui/words';
import {
  BASIS_WORDS,
  CAMPAIGN_END_WORDS,
  EVIDENCE_WORDS,
  EXCLUSION_WORDS,
  LEAD_WORDS,
  LEGAL_BASIS_WORDS,
  MAILING_END_WORDS,
  PACE_WORDS,
  STAGE_WORDS,
  SUPPRESSION_WORDS,
  SYNC_CHIP,
  WAY_IN_WORDS,
  liftsWithTime,
  resumesIn,
  withoutReferences,
} from './words';

/**
 * What each outreach record says about itself (ADR-0155): the campaign, the
 * first email waiting, the template, the ask to lift a stop, the mailing and
 * its asks, the person on the lifecycle list, the country, the restaurant
 * and the week's count.
 *
 * Pure: each takes the row the screen already holds and a `Who`, and says
 * what it is, why it is in front of this reader, where it came from, who
 * has it, and the best next step by its state. Nothing is fetched or
 * guessed. What the wire does not carry (who drew up a campaign, which plan
 * found a restaurant) is `null`, which the panel reads as "Not recorded".
 * Nothing here says the platform sends: a first email, a cadence step or a
 * mailing that goes out is a person's act, or a department level's that a
 * lead approved, and every one is judged again as it leaves.
 */

const DAY = 86_400_000;

export const withRole = (p: Party | null, role: string): Party | null => (p ? { ...p, role } : null);
export const nameOf = (who: Who, id: string | null | undefined): string | null => who.actor(id)?.name ?? null;
export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;
const sentence = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const percent = (part: number, whole: number): number => Math.round((100 * part) / whole);
/** A person's free text, cut so a long line stays a line. */
const cut = (s: string, n = 400): string => (s.length > n ? `${s.slice(0, n).trimEnd()}…` : s);

export interface DeptCtx {
  /** The department whose list this is: where its lead and agents are found. */
  department: string;
  /** The department's lead, or a superadmin: who approves and decides here. */
  mayDecide: boolean;
}

// --- Campaigns ---------------------------------------------------------------

export function explainCampaign(c: WireCampaign, who: Who, ctx: DeptCtx & { now?: number; onOpen?: () => void }): ItemContext {
  const now = ctx.now ?? Date.now();
  const lead = who.lead(ctx.department);
  const leadName = lead && lead.name !== 'You' ? lead.name : 'the department’s lead';
  const daysLeft = Math.ceil((Date.parse(c.validTo) - now) / DAY);
  const open: StepAction[] = ctx.onOpen ? [{ label: ctx.mayDecide && c.state === 'draft' ? 'Read it and approve' : 'Show its terms', run: ctx.onOpen, primary: c.state === 'draft' && ctx.mayDecide }] : [];

  let next: ItemContext['next'];
  if (c.state === 'draft') {
    next = ctx.mayDecide
      ? {
          tip: 'Read the email as it would go, then approve it. Before you can, a person must have sent one of the agent’s drafts from the shared box and watched it arrive under the agent’s name; approval then takes a single-use token.',
          instead: 'Or leave it as a draft: a draft sends nothing, and the agent keeps leaving first emails for a person to send.',
          actions: open,
        }
      : {
          tip: `Nothing is needed from you. ${sentence(leadName)} approves a campaign once it reads right; until then it sends nothing.`,
          instead: 'If the wording or the countries look wrong, tell the lead before they approve it.',
          actions: open,
        };
  } else if (c.state === 'approved') {
    const running = daysLeft < 0 ? 'Its dates have passed, so nothing more goes out under it.' : `It runs until ${when(c.validTo)} (${plural(daysLeft, 'day')} left): ${c.sent} of ${c.ceiling} sent.`;
    next = ctx.mayDecide
      ? {
          tip: `Nothing is needed while it runs. ${running} Watch the replies and who says stop; each email is still judged as it leaves.`,
          instead: 'If the wording turns out wrong, end it from its terms: emails already sent are not recalled, and the planned ones are cancelled.',
          actions: open,
        }
      : {
          tip: `Nothing is needed from you. ${running} The platform judges every email again as it leaves, and replies arrive in Mail for a person to answer.`,
          actions: open,
        };
  } else {
    const reason = c.endedReason;
    next =
      reason === 'breach'
        ? {
            tip: 'It stopped itself because too many first emails bounced or were complained about. Nothing to do on the campaign; a superadmin has the incident, with the reading.',
            instead: 'Draw up a new one only once the cause is understood. Until then the agent writes drafts a person sends.',
            actions: open,
          }
        : {
            tip: `It is over (${reason ? CAMPAIGN_END_WORDS[reason].toLowerCase() : 'ended'}). Nothing to do; its count stays as the record of what it sent.`,
            instead: ctx.mayDecide ? 'If the same wording should go again, draw up a new campaign with new dates.' : undefined,
            actions: open,
          };
  }

  return {
    subject: { kind: 'campaign', title: c.name },
    summary: `One email template that the department’s agent fills with a paragraph about each restaurant, for ${countryNames(c.markets)}: up to ${c.ceiling} first emails, ${when(c.validFrom)} to ${when(c.validTo)}. ${c.sent} sent, ${c.planned} planned, ${c.remaining} left.`,
    need:
      c.state === 'draft'
        ? 'Approval, once, by the department’s lead or a superadmin, under a single-use token.'
        : c.state === 'approved'
          ? 'Nothing, while it runs. It can be ended by the lead at any time.'
          : 'Nothing: it has ended.',
    why:
      c.state === 'draft'
        ? ctx.mayDecide
          ? 'You lead the department (or are a superadmin), so approving it is yours.'
          : 'It is in your department’s list; only the lead or a superadmin approves it.'
        : 'It is in your department’s list of what the agent may send on its own.',
    source: { label: 'Drawn up on this page', when: when(c.createdAt) },
    // Who drew it up (0162); an older reply that does not carry it leaves the row out.
    broughtBy: c.createdBy === undefined ? undefined : c.createdBy === null ? null : (() => {
      const author = who.actor(c.createdBy);
      return author ? { ...author, role: 'drew it up', when: when(c.createdAt) } : null;
    })(),
    owner: c.approvedBy ? withRole(who.actor(c.approvedBy), 'approved it') : lead,
    involved: c.state === 'approved' ? who.agentsOf(ctx.department) : [],
    knows: who.knowers(ctx.department),
    facts: [
      ['Countries', countryNames(c.markets)],
      ['Fits', c.minTier === 'strong' ? 'Strong fits only' : 'Strong and possible fits'],
      ['Addresses', c.addressKinds.map((k) => (k === 'role' ? 'shared' : 'a person’s')).join(' or ')],
      ['Approved', c.approvedAt ? when(c.approvedAt) : null],
      ['Ended', c.endedReason ? `${CAMPAIGN_END_WORDS[c.endedReason]}${c.endedAt ? `, ${when(c.endedAt)}` : ''}` : null],
    ],
    next,
  };
}

// --- First emails waiting ----------------------------------------------------

export function explainFirstTouch(t: WireQueuedTouch, who: Who, ctx: DeptCtx): ItemContext {
  const name = t.prospect.businessName ?? t.prospect.domain ?? 'a restaurant';
  const agents = who.agentsOf(ctx.department);
  // The agent that wrote it, when the queue says (0162); else the department's only one.
  const agent = (t.draftedBy ? who.actor(t.draftedBy) : null) ?? (agents.length === 1 ? agents[0]! : null);
  const pace = t.authority.pace;
  const paceWords = pace ? PACE_WORDS[pace.code as PaceRefusalCode] : undefined;
  // The draft's own link is the panel's "Open the draft in Mail", so the step adds none.
  const open: StepAction[] = [];
  const campaign = t.campaign?.name ? `“${t.campaign.name}”` : t.campaign ? 'a campaign' : null;

  let next: ItemContext['next'];
  if (t.footer === null) {
    next = {
      tip: 'This email could not be composed from its record, so it has no footer and will not leave as it is. Open the draft and look at it, and ask the department’s lead or agent why.',
      instead: 'If it cannot be put right, throw the draft away; the restaurant stays on the list.',
      actions: open,
    };
  } else if (t.authority.unattended) {
    next = {
      tip: `It is planned${t.scheduledFor ? ` for ${when(t.scheduledFor)}` : ''} under ${campaign ?? 'an approved campaign'} and may leave on its own at the recipient’s working hour. Read it first if you want to; the hour, the daily limit and who said stop are checked again as it goes.`,
      instead: 'If it should not go, end the campaign (the lead can) or open the draft and throw it away before its time.',
      actions: open,
    };
  } else if (paceWords && !liftsWithTime(pace!.code as PaceRefusalCode) && paceWords.who === 'nobody') {
    next = {
      tip: `It would be refused now: ${paceWords.title.toLowerCase()}. Nothing here changes that, because the law there decides. Open the draft and rewrite it for what is allowed, or throw it away.`,
      instead: 'Nobody sends it as it stands; the platform refuses it again on every attempt.',
      actions: open,
    };
  } else if (paceWords && paceWords.who === 'a person') {
    next = {
      tip: `It cannot go yet: ${paceWords.title.toLowerCase()}. That is for a person to change in the markets file${ctx.mayDecide ? ' — you lead the department, so it is yours to ask for' : ' — ask the department’s lead'}. Until then, do not send it.`,
      instead: 'Or open the draft and throw it away if this country should not be written to at all.',
      actions: open,
    };
  } else if (paceWords) {
    next = {
      tip: `Not yet: ${paceWords.title.toLowerCase()}${pace?.resumesAt ? `; it opens again on ${when(pace.resumesAt)}` : ''}. Waiting is the whole remedy. Then open the draft, read it, edit it as you like and send it yourself.`,
      instead: 'Or throw the draft away if it should not go. The platform never sends it for you.',
      actions: open,
    };
  } else {
    next = {
      tip: 'Open the draft in Mail, read it, edit what you want and press send yourself — or throw it away if it should not go. A person sends this one; the platform does not.',
      instead: 'If you leave it, nothing happens: it stays a draft in the shared box.',
      actions: open,
    };
  }

  return {
    subject: { kind: 'first email', title: name },
    summary: `A first email to ${name} in ${countryName(t.prospect.marketCode)}, written by the department’s agent from the restaurant’s own website and not yet sent.${campaign ? ` It is planned under ${campaign}.` : ' No campaign covers it.'}`,
    need: t.authority.unattended ? 'Nothing: it may leave on its own at the recipient’s working hour.' : 'A person reads it in the shared box, edits it if needed, and sends it from there — or throws it away.',
    why: withoutReferences(t.authority.reason) || 'It is the platform’s own count of what waits to go.',
    source: { label: `The restaurant list: ${name}`, to: `/prospects/${t.prospect.id}` },
    broughtBy: agent,
    owner: t.authority.unattended ? agent : null,
    involved: agents,
    knows: who.knowers(ctx.department),
    facts: [
      ['Country', countryName(t.prospect.marketCode)],
      ['Campaign', t.campaign?.name ?? null],
      ['Planned', t.scheduledFor ? when(t.scheduledFor) : 'When a person sends it'],
      ['Held back by', paceWords ? paceWords.title : null],
    ],
    next,
    open: { to: `/mail/${t.threadId}`, label: 'Open the draft in Mail' },
  };
}

// --- First-touch templates ---------------------------------------------------

export function explainSequence(s: WireSequence, who: Who, ctx: DeptCtx & { act?: () => void }): ItemContext {
  const lead = who.lead(ctx.department);
  const language = languageName(s.lang);
  const leadName = lead && lead.name !== 'You' ? lead.name : 'the department’s lead';
  let next: ItemContext['next'];
  // Its reader (0155): a template waits for the colleague asked to read it, and one they say needs changes is written again.
  const read = s.read ?? 'unread';
  if (s.state === 'draft' && read === 'open') {
    next = {
      tip: 'Somebody was asked to read it in the language. It is approved once they have answered that it reads well; until then approving it is refused.',
      instead: 'If you no longer want their reading, write the template again; its words are kept as written.',
    };
  } else if (s.state === 'draft' && read === 'needs_changes') {
    next = {
      tip: 'Its reader said it needs changes, and why. Write a corrected template and ask them to read that one; this one is not approved.',
      instead: 'Its words are kept as written, so a change is always a new template.',
    };
  } else if (s.state === 'draft') {
    next = ctx.mayDecide
      ? {
          tip: 'Read the subject and the words below as they stand, then approve them as written: pressing its row approves it at once, and a template is never edited afterwards. Approving lets nothing leave on its own.',
          instead: 'If a word is wrong, leave it and write a corrected template: only an approved one is used, and a waiting one is simply never approved.',
          actions: ctx.act ? [{ label: 'Approve it as written', run: ctx.act, primary: true }] : [],
        }
      : {
          tip: `Nothing is needed from you. ${sentence(leadName)} approves it as written; until then no first email starts from it.`,
          instead: 'If you see a mistake in it, tell the lead before they approve.',
        };
  } else if (s.state === 'approved') {
    next = ctx.mayDecide
      ? {
          tip: `It is in use: first emails in ${language} that no campaign covers start from the newest approved template. Nothing is needed unless the wording is wrong.`,
          instead: 'To change it, retire it and write a new one; templates are retired, never edited.',
          actions: ctx.act ? [{ label: 'Retire it', run: ctx.act }] : [],
        }
      : {
          tip: `Nothing is needed from you. First emails in ${language} start from it, and each still waits for a person to send it.`,
        };
  } else {
    next = {
      tip: 'It is retired and no longer used. Nothing to do.',
      instead: `If you need these words again, write a new template in ${language}.`,
    };
  }
  return {
    subject: { kind: 'template', title: s.name },
    summary: `The words a first email in ${language} starts from when no campaign covers the restaurant. The agent writes one paragraph (at most ${s.paragraphWords} words); the rest is as approved. Subject: “${s.templateSubject}”.`,
    need: s.state === 'draft' && read === 'open' ? 'Its reader’s answer, before anyone can approve it.' : s.state === 'draft' && read === 'needs_changes' ? 'A corrected template: its reader said this one needs changes.' : s.state === 'draft' ? 'Approval as written, by the department’s lead or a superadmin.' : s.state === 'approved' ? 'Nothing, while it is in use.' : 'Nothing.',
    why: 'It is in your department’s list of templates, which the first emails are written from.',
    source: { label: 'Written on this page', when: when(s.createdAt) },
    broughtBy: { ...(who.actor(s.createdBy) ?? { name: 'Someone in the department', kind: 'person' as const }), when: when(s.createdAt) },
    owner: s.approvedBy ? withRole(who.actor(s.approvedBy), 'approved it') : s.state === 'draft' ? lead : null,
    involved: s.state === 'approved' ? who.agentsOf(ctx.department) : [],
    knows: who.knowers(ctx.department),
    facts: [
      ['The email', cut(s.templateBody)],
      ['Read by a native reader', s.state === 'draft' && read !== 'unread' ? { open: 'Being read', reads_well: 'Reads well', needs_changes: 'Needs changes' }[read] : null],
      ['Approved', s.approvedAt ? when(s.approvedAt) : null],
      ['Retired', s.retiredAt ? when(s.retiredAt) : null],
    ],
    next,
  };
}

// --- Who said stop -----------------------------------------------------------

export function explainLiftRequest(r: WireLiftRequest, who: Who, ctx: { department: string; superadmin: boolean; onDecide?: () => void }): ItemContext {
  const mine = who.actor(r.requestedBy)?.name === 'You';
  const stoppedFor = r.suppressedFor ? SUPPRESSION_WORDS[r.suppressedFor].toLowerCase() : null;
  let next: ItemContext['next'];
  if (r.state === 'requested') {
    next = ctx.superadmin
      ? {
          tip: 'Check that the evidence is a real opt-in made after they said stop: a dated, signed or written yes. If it is, lift the stop under a single-use token; if it is not, decline with a reason so the person who asked can put it right.',
          instead: 'Declining keeps them on the list and tells the person who asked why. Until you decide, nothing is sent to them.',
          actions: ctx.onDecide ? [{ label: 'Decide it', run: ctx.onDecide, primary: true }] : [],
        }
      : {
          tip: mine
            ? 'Nothing more for you to do now. A superadmin checks your evidence and decides; until then nothing is sent to them.'
            : 'Nothing is needed from you. A superadmin decides it.',
          instead: 'If the evidence can be stronger (a signed card, a dated message), tell the superadmin before they decide.',
        };
  } else if (r.state === 'lifted') {
    next = {
      tip: 'Done: they may be written to again like anyone else, and what the stop said is kept beside the lift. Nothing to do.',
      instead: 'If they say stop again, that is recorded at once and nothing is sent.',
    };
  } else {
    next = {
      tip: mine
        ? `Declined${r.refusalReason ? `: “${r.refusalReason}”` : ''}. They stay on the list. If you get stronger evidence, record a new ask.`
        : 'Declined; they stay on the list. Nothing to do.',
    };
  }
  return {
    subject: { kind: 'ask to lift a stop', title: r.evidence },
    summary: `Someone who asked us to stop${stoppedFor ? ` (${stoppedFor})` : ''} has opted in again, according to ${nameOf(who, r.requestedBy) ?? 'a colleague'}. The address is not shown anywhere: the list holds only a hash of it.`,
    need:
      r.state === 'requested'
        ? 'A superadmin’s decision: lift the stop under a single-use token, or decline with a reason.'
        : r.state === 'lifted'
          ? 'Nothing: it was lifted.'
          : 'Nothing: it was declined.',
    why: ctx.superadmin
      ? r.state === 'requested'
        ? 'Every ask to lift a stop waits for a superadmin, which is you.'
        : 'You can see every ask to lift a stop.'
      : mine
        ? 'You recorded it, so you can follow what comes of it.'
        : 'It is shown to you because it concerns your department’s list.',
    source: { label: 'Recorded on Who said stop, with the evidence of the new opt-in', when: when(r.requestedAt) },
    broughtBy: { ...(who.actor(r.requestedBy) ?? { name: 'A colleague', kind: 'person' as const }), when: when(r.requestedAt) },
    owner: r.decidedBy ? withRole(who.actor(r.decidedBy), r.state === 'lifted' ? 'lifted it' : 'declined it') : null,
    knows: who.knowers(ctx.department),
    facts: [
      ['The new opt-in', r.evidence],
      ['Stopped for', r.suppressedFor ? `${SUPPRESSION_WORDS[r.suppressedFor]}${r.suppressedAt ? `, ${when(r.suppressedAt)}` : ''}` : null],
      ['Decided', r.decidedAt ? when(r.decidedAt) : null],
      ['Why declined', r.refusalReason],
    ],
    next,
  };
}

/** The register as a whole: counts, never addresses. */
export function explainRegister(counts: WireSuppressionCounts | undefined, who: Who, ctx: { department: string }): ItemContext {
  const total = counts?.total ?? 0;
  const top = [...(counts?.byReason ?? [])].sort((a, b) => b.count - a.count)[0];
  const complained = counts?.byReason.find((r) => r.reason === 'complained')?.count ?? 0;
  return {
    subject: { kind: 'list', title: 'Who said stop' },
    summary: `Everyone who unsubscribed, asked us to stop, bounced or marked a message as spam${counts ? `: ${plural(total, 'address', 'addresses')}` : ''}. The list keeps a hash of each address, so nobody can read one back.${top && total > 0 ? ` Most common: ${SUPPRESSION_WORDS[top.reason].toLowerCase()} (${top.count} of ${total}).` : ''}`,
    need: 'Nothing from you to keep it current: a stop is recorded at once, from a reply, an unsubscribe, a bounce or a complaint, or by a person adding one.',
    why: 'Nothing is sent to anyone on it, by an agent or by a person pressing send, so it is the first place to look before writing to a list you were given.',
    source: { label: 'Every unsubscribe, objection, bounce, complaint and manual addition across outreach' },
    broughtBy: null,
    knows: who.knowers(ctx.department),
    facts: (counts?.byReason ?? []).map((r) => [SUPPRESSION_WORDS[r.reason], String(r.count)] as [string, string]),
    next: {
      tip:
        total === 0
          ? 'Nothing is needed. When the first person asks us to stop, they appear here as a number, never as an address.'
          : 'Before you write to someone from a list you were given, look their address up below: yes or no, and nothing is kept.',
      instead: `${complained > 0 ? `${plural(complained, 'complaint')} count towards the threshold that ends a campaign. ` : ''}To take someone off, record their new opt-in under “Asked to hear from us again”; a superadmin decides it.`,
    },
  };
}

// --- Mailings ----------------------------------------------------------------

export function explainMailing(m: WireMailing, who: Who, ctx: DeptCtx & { now?: number; onOpen?: () => void }): ItemContext {
  const now = ctx.now ?? Date.now();
  const lead = who.lead(ctx.department);
  const leadName = lead && lead.name !== 'You' ? lead.name : 'the department’s lead';
  const total = m.sent + m.asked + m.refused;
  const open: StepAction[] = ctx.onOpen ? [{ label: m.state === 'draft' && ctx.mayDecide ? 'Read it and approve' : 'Show its terms and asks', run: ctx.onOpen, primary: m.state === 'draft' && ctx.mayDecide }] : [];
  const goesTo = m.bases.map((b) => BASIS_WORDS[b].title.toLowerCase()).join(' or ');
  const mine = who.actor(m.createdBy)?.name === 'You';

  let next: ItemContext['next'];
  if (m.state === 'draft') {
    next = ctx.mayDecide
      ? {
          tip: 'Read the words as they are, then approve them. You need to have watched one of the agent’s drafts arrive from the shared box under the agent’s name first; approval takes a single-use token.',
          instead: 'Until it is approved, a journey’s ask for it is refused and nothing is sent.',
          actions: open,
        }
      : {
          tip: mine ? `Nothing more for you to do. ${sentence(leadName)} approves it; until then a journey’s ask for it is refused.` : `Nothing is needed from you. ${sentence(leadName)} approves it.`,
          actions: open,
        };
  } else if (m.state === 'approved') {
    const expired = Date.parse(m.validTo) < now;
    next =
      m.refused > 0
        ? {
            tip: `${m.refused} of ${total} journey ask${m.refused === 1 ? ' was' : 's were'} refused. Open its asks: each says why. If the cause is something you can fix, such as a basis not yet recorded on Lifecycle, fix it, and the journey has to ask again, because the platform sends nothing on its own.`,
            instead: ctx.mayDecide || mine ? 'If the mailing itself is wrong, end it; waiting asks are then refused.' : undefined,
            actions: open,
          }
        : {
            tip: expired
              ? 'Its dates have passed, so a journey’s ask is refused now. Nothing to do; write a new mailing if it should go on.'
              : `Nothing is needed. Customer.io decides when it goes; the platform sends it only to someone who is ${goesTo} when it goes, and never to anyone who said stop. ${m.asked > 0 ? `${plural(m.asked, 'ask is', 'asks are')} waiting to be judged.` : ''}`.trim(),
            instead: ctx.mayDecide || mine ? 'If the words turn out wrong, end it from its terms.' : undefined,
            actions: open,
          };
  } else {
    next = {
      tip: `It is over (${m.endedReason ? MAILING_END_WORDS[m.endedReason].toLowerCase() : 'ended'}). Nothing to do.`,
      instead: 'A journey that still asks for it is refused. Write a new mailing if the words should go again.',
      actions: open,
    };
  }

  return {
    subject: { kind: 'mailing', title: m.name },
    summary: `An email a Customer.io journey may ask the platform to send, as the department’s agent from the shared box, to people who ${goesTo}. Customer.io decides when; the words are the approved ones. Up to ${m.perDayCeiling} a day and ${m.ceiling} in all, ${when(m.validFrom)} to ${when(m.validTo)}.`,
    need: m.state === 'draft' ? 'Approval, once, by the department’s lead or a superadmin, under a single-use token.' : m.state === 'approved' ? 'Nothing, while it is in use.' : 'Nothing: it has ended.',
    why: m.state === 'draft' ? (ctx.mayDecide ? 'You lead the department (or are a superadmin), so approving it is yours.' : 'It is your department’s; only the lead or a superadmin approves it.') : 'It is in your department’s list of what journeys may ask for.',
    source: { label: 'Written on this page', when: when(m.createdAt) },
    broughtBy: { ...(who.actor(m.createdBy) ?? { name: 'Someone in the department', kind: 'person' as const }), when: when(m.createdAt) },
    owner: m.approvedBy ? withRole(who.actor(m.approvedBy), 'approved it') : lead,
    involved: m.state === 'approved' ? [who.actor(m.agentEmail) ?? who.agentsOf(ctx.department)[0]].filter((p): p is Party => Boolean(p)) : [],
    knows: who.knowers(ctx.department),
    facts: [
      ['Language', languageName(m.lang)],
      ['Sent', String(m.sent)],
      ['Waiting', String(m.asked)],
      ['Refused', String(m.refused)],
      ['Approved', m.approvedAt ? when(m.approvedAt) : null],
      ['Ended', m.endedReason ? `${MAILING_END_WORDS[m.endedReason]}${m.endedAt ? `, ${when(m.endedAt)}` : ''}` : null],
    ],
    next,
  };
}

export function explainMailingRequest(r: WireMailingRequest, who: Who, ctx: { department: string; mailing?: string }): ItemContext {
  const state = r.state;
  const next: ItemContext['next'] =
    state === 'asked'
      ? { tip: 'Nothing is needed. It waits for the heartbeat to judge it against the person’s basis, who said stop, the department’s level and the box’s room.' }
      : state === 'sent'
        ? { tip: 'Nothing to do. It was sent from the shared box as the agent; a reply arrives in Mail for a person to answer.' }
        : {
            tip: `Read why it was refused${r.reason ? `: “${withoutReferences(r.reason)}”` : ''}. If the cause is something you can fix, such as recording the person’s basis on Lifecycle, fix it; the journey then has to ask again, because the platform sends nothing on its own.`,
            instead: 'If the reason is that they said stop or hold no basis, the refusal was right: nothing to do.',
          };
  return {
    subject: { kind: 'journey ask', title: ctx.mailing ? `An ask for “${ctx.mailing}”, ${when(r.askedAt)}` : `An ask from ${when(r.askedAt)}` },
    summary: `A Customer.io journey asked for ${ctx.mailing ? `“${ctx.mailing}”` : 'this mailing'} to be sent to one person, who is not named here. ${state === 'asked' ? 'It has not been judged yet.' : state === 'sent' ? 'It was sent.' : 'It was refused.'}`,
    need: state === 'refused' ? 'Reading why, and fixing the cause if it is yours to fix.' : 'Nothing.',
    why: 'Every ask a journey makes is kept with what came of it, so a refusal is never silent.',
    source: { label: 'A Customer.io journey’s step asked for it', when: when(r.askedAt) },
    knows: who.knowers(ctx.department),
    facts: [
      ['Decided', r.decidedAt ? when(r.decidedAt) : null],
      ['Why not', r.reason ? withoutReferences(r.reason) : null],
    ],
    next,
  };
}

// --- Lifecycle ---------------------------------------------------------------

export function explainLifecycleContact(c: WireLifecycleContact, who: Who, ctx: { department: string; holder: string | null; onOpen?: () => void }): ItemContext {
  const bases = c.bases.map((b) => BASIS_WORDS[b].title);
  const title = `${bases.length > 0 ? bases[0] : 'No basis standing'}${c.marketCode ? `, ${countryName(c.marketCode)}` : ''}`;
  const earliest = [...c.history].sort((a, b) => Date.parse(a.recordedAt) - Date.parse(b.recordedAt))[0] ?? null;
  const holder = ctx.holder ? who.actor(ctx.holder) : null;
  const open: StepAction[] = ctx.onOpen ? [{ label: 'Show what was recorded', run: ctx.onOpen }] : [];
  const sync = SYNC_CHIP[c.syncState].label.toLowerCase();

  let next: ItemContext['next'];
  if (c.suppressed) {
    next = {
      tip: 'Nothing to do. They said stop, which outranks every basis: nothing is sent to them, and Customer.io is told only that they unsubscribed.',
      instead: 'If they say yes again with evidence, record the ask on Who said stop; a superadmin decides it.',
      actions: open,
    };
  } else if (c.syncState === 'erasing' || c.syncState === 'deleted') {
    next = { tip: c.syncState === 'erasing' ? 'Nothing to do. They are being erased from Customer.io.' : 'Nothing to do. They have been erased from Customer.io, and nothing is sent.', actions: open };
  } else if (c.bases.length === 0) {
    next = {
      tip: 'No basis stands, so Customer.io is not told about them, and is brought up to date on the next heartbeat if it already holds them. If they have since said yes or asked for something, record that from the message or the order.',
      instead: 'Otherwise nothing to do.',
      actions: open,
    };
  } else if (c.syncState === 'none') {
    next = ctx.holder
      ? {
          tip: c.lastError ? `Not in Customer.io yet: ${c.lastError} Fix what it says, or open the record and bring Customer.io up to date now.` : 'It goes to Customer.io on the next heartbeat. Nothing is needed.',
          actions: open,
        }
      : {
          tip: 'Nothing for you: the basis is recorded and kept, and nothing reaches Customer.io until a superadmin assigns it.',
          instead: 'If it is urgent, ask a superadmin to assign Customer.io.',
          actions: open,
        };
  } else if (c.syncState === 'unsubscribed') {
    next = { tip: 'They unsubscribed in Customer.io. Nothing is sent to them; nothing to do unless they ask to hear from us again.', actions: open };
  } else {
    next = {
      tip: 'Nothing is needed. They are in Customer.io. When something happens to them, such as a shipped sample, open the record and tell Customer.io, which judges it against their basis then.',
      actions: open,
    };
  }

  return {
    subject: { kind: 'person on the lifecycle list', title },
    summary: `Somebody Customer.io may be told about, known only by a sealed address. ${bases.length > 0 ? `Basis: ${bases.join('; ').toLowerCase()}.` : 'No basis stands.'}${c.leadType ? ` Interest: ${LEAD_WORDS[c.leadType].toLowerCase()}.` : ''} With Customer.io: ${c.suppressed ? 'said stop' : sync}.`,
    need: c.suppressed ? 'Nothing: the stop outranks everything.' : c.bases.length === 0 ? 'A basis to be recorded, if they have given one.' : 'Nothing, unless an event should be told to Customer.io.',
    why: c.suppressed
      ? 'A stop rule applies: they said stop, so nothing is sent.'
      : c.bases.length === 0
        ? 'Their last basis was withdrawn, so the list keeps them only as a record.'
        : 'They are on the list because a basis is on record with its evidence.',
    source: earliest
      ? { label: `${EVIDENCE_WORDS[earliest.evidenceKind]}: ${earliest.source}`, when: when(earliest.recordedAt) }
      : null,
    broughtBy: earliest ? { ...(who.actor(earliest.recordedBy) ?? { name: 'A colleague', kind: 'person' as const }), when: when(earliest.recordedAt) } : null,
    owner: undefined,
    involved: holder ? [{ ...holder, role: 'holds Customer.io' }] : [],
    knows: who.knowers(ctx.department),
    facts: [
      ['Country', c.marketCode ? countryName(c.marketCode) : null],
      ['Interest', c.leadType ? LEAD_WORDS[c.leadType] : null],
      ['Customer.io', c.syncedAt ? `${sync} since ${when(c.syncedAt)}` : 'Not sent yet'],
      ['Bases recorded', plural(c.history.length, 'time')],
      ['Why not yet', c.lastError],
    ],
    next,
  };
}

// --- Countries ---------------------------------------------------------------

export function explainMarketDay(m: WireMarketDay, who: Who, ctx: DeptCtx & { onOpen?: () => void }): ItemContext {
  const lead = who.lead(ctx.department);
  const refusal = m.now.refusal;
  const words = refusal ? PACE_WORDS[refusal.code] : null;
  const open: StepAction[] = ctx.onOpen ? [{ label: 'Show its hours, holidays and law', run: ctx.onOpen }] : [];
  const p = m.policy;

  let next: ItemContext['next'];
  if (!m.declared) {
    next = {
      tip: 'Nothing is needed from you today. To start writing to this country, a person commits it to the markets file with its legal basis confirmed.',
      instead: 'Until then no first email leaves on its own there; drafts a person sends are the only way.',
      actions: open,
    };
  } else if (m.now.sendable) {
    next = {
      tip: `Open now: first emails planned for ${m.name} can leave in its working hours${p ? ` (${m.sentToday} of ${p.perDayCeiling} sent today)` : ''}. Nothing to do; each is still judged as it goes.`,
      actions: open,
    };
  } else if (refusal && words && liftsWithTime(refusal.code)) {
    next = {
      tip: `Not yet: ${words.title.toLowerCase()}.${refusal.resumesAt ? ` It opens again ${resumesIn(refusal.resumesAt, m.now.zone)}.` : ''} Nothing to do; waiting is the whole remedy.`,
      actions: open,
    };
  } else if (refusal && words && words.who === 'nobody') {
    next = {
      tip: `Closed, and it does not lift with time: ${words.title.toLowerCase()}. The law there decides, so nothing here changes it.`,
      instead: 'First emails here need a different route; a person can still write a draft that fits what is allowed.',
      actions: open,
    };
  } else {
    next = {
      tip: `Closed: ${words ? words.title.toLowerCase() : 'the platform would refuse a first email now'}. That is for a person to change in the markets file${ctx.mayDecide ? ' — as the lead, you can ask for it' : ''}; nothing is needed from you unless you are asking for it.`,
      instead: 'Until then no first email leaves on its own, and a person sending a draft is refused too.',
      actions: open,
    };
  }

  const confirmer = p?.confirmedBy ? withRole(who.actor(p.confirmedBy), 'confirmed the law') : null;
  return {
    subject: { kind: 'country', title: m.name },
    summary: !m.declared
      ? `${m.name} is not a country we write to yet: nobody has written down which law applies, so no first email can leave there.`
      : `${m.name}: ${p ? LEGAL_BASIS_WORDS[p.legalBasis].toLowerCase() : 'its law is not written down yet'}. ${p ? `Up to ${p.perDayCeiling} first emails a day, at least ${p.minGapMinutes} minutes apart.` : ''} It is ${m.now.localDate} there now.`.replace(/\s+/g, ' ').trim(),
    need: !m.declared ? 'A person to commit the country to the markets file with its legal basis confirmed.' : m.now.sendable ? 'Nothing.' : words?.who === 'time' ? 'Nothing: only time.' : words?.who === 'nobody' ? 'Nothing can lift it.' : 'A person to change the markets file.',
    why: 'Every first email is judged by the same calendar before it leaves, so this says what would happen to one now.',
    source: p ? { label: 'The markets file in the ledger, committed by a person' } : null,
    broughtBy: confirmer,
    owner: lead,
    knows: who.knowers(ctx.department),
    facts: [
      ['Today there', m.now.localDate],
      ['Sent today', p ? `${m.sentToday} of ${p.perDayCeiling}` : null],
      ['Next day off', (() => { const d = m.ahead.find((h) => h.refused); return d ? `${d.date}, ${d.name}` : null; })()],
    ],
    next,
  };
}

// --- Restaurants -------------------------------------------------------------

const TIER_WORDS = { strong: 'a strong fit', possible: 'a possible fit', weak: 'a weak fit', excluded: 'not a fit' } as const;

export function explainProspect(p: WireProspect, who: Who, ctx: { department: string; outreach: boolean; mayDecide?: boolean }): ItemContext {
  const name = p.businessName ?? p.domain ?? 'A restaurant';
  const agents = who.agentsOf(ctx.department);
  const way = WAY_IN_WORDS[p.wayIn ?? 'cold'];
  const where = [countryName(p.marketCode), p.territory ?? p.zone].filter(Boolean).join(', ');
  const found =
    p.source === 'places'
      ? p.segment
        ? `The “${p.segment}” search${p.territory ? ` in ${p.territory}` : ''}, on Google Maps`
        : 'A search on Google Maps'
      : p.source === 'base_list'
        ? 'A list a person imported'
        : 'Market research';
  const open: StepAction[] = p.threadId && p.stage === 'drafted' && ctx.outreach ? [{ label: 'Open the draft in Mail', to: `/mail/${p.threadId}`, primary: true }] : [];

  let next: ItemContext['next'];
  if (p.stage === 'excluded') {
    const why = p.exclusion;
    next =
      why === 'suppressed'
        ? { tip: 'Nothing to do, and nothing will be sent: its address asked us to stop.', instead: 'If they ask to hear from us again, record that on Who said stop.' }
        : why === 'by_person'
          ? { tip: 'Nothing to do: a person took it off the list.', instead: 'If that was a mistake, find it again with a new search.' }
          : { tip: `Nothing to do: ${why ? EXCLUSION_WORDS[why].toLowerCase() : 'it was taken off the list'}.`, instead: 'It is kept so the same place is not found and worked twice.' };
  } else if (p.fit && (p.fit.tier === 'weak' || p.fit.tier === 'excluded')) {
    next = {
      tip: `It scores ${p.fit.score}, ${TIER_WORDS[p.fit.tier]}, so it is below the bar. Nothing to do unless you know something its website does not say.`,
      instead: 'Open it to see each point the score rests on, and take it off the list if you agree.',
    };
  } else if (!ctx.outreach) {
    next = {
      tip: 'Nothing is sent from here: a person decides what to do with each place. Open it to see why it scored what it did and who might introduce you.',
      instead: way.label === 'Cold' ? 'Nobody here knows them yet; look for a warm way in before reaching out.' : `A way in exists: ${way.says}.`,
    };
  } else if (p.stage === 'discovered' || p.stage === 'read') {
    next = {
      tip: p.stage === 'discovered' ? 'Nothing is needed: its website is read next, on the heartbeat.' : 'Nothing is needed: a shared address is looked for next, on the heartbeat.',
      instead: 'If it stays here for long, open it and press Try again.',
    };
  } else if (p.stage === 'enriched') {
    next = {
      tip: 'It has a checked address and no first email yet. Open its record and press “Write the first email now”; the draft waits in the shared box for a person to send.',
      instead: way.label === 'Cold' ? 'Or take it off the list if it should not be written to.' : `A way in exists (${way.says}): ask for an introduction before a first email goes cold.`,
    };
  } else {
    next = {
      tip: 'A first email is waiting in the shared box. Open the draft, read it, edit what you want and send it yourself — or throw it away if it should not go.',
      instead: way.label === 'Cold' ? 'The platform sends nothing; if you leave it, it stays a draft.' : `A way in exists (${way.says}); an introduction may land better than a cold email.`,
      actions: open,
    };
  }

  return {
    subject: { kind: 'restaurant', title: name },
    summary: `${name}${where ? `, ${where}` : ''}. Found by: ${found.charAt(0).toLowerCase()}${found.slice(1)}. ${p.fit ? `Scored ${p.fit.score}, ${TIER_WORDS[p.fit.tier]}.` : 'Not scored yet.'} ${STAGE_WORDS[p.stage]}.`,
    need:
      p.stage === 'excluded'
        ? 'Nothing: it is off the list.'
        : !ctx.outreach
          ? 'A person’s decision on what to do with it; nothing is sent from here.'
          : p.stage === 'enriched'
            ? 'A first email to be written; the draft is then a person’s to send.'
            : p.stage === 'drafted'
              ? 'A person to read the draft and send it, or throw it away.'
              : 'Nothing yet: the platform is still reading it.',
    why: p.whyNow ?? (p.fit ? `It scored ${p.fit.score}, ${TIER_WORDS[p.fit.tier]}, against the profile’s rules.` : 'It was found by a search and is waiting to be read.'),
    source: { label: found, when: when(p.createdAt), ...(p.planId ? { to: `/prospecting/plans/${p.planId}` } : {}) },
    // The agent whose sweep found it (0162). An import or a report names
    // nobody on the row, so the panel says so rather than guessing.
    broughtBy: (() => {
      if (p.foundBy === undefined) return undefined;
      const finder = who.actor(p.foundBy);
      return finder ? { ...finder, role: p.planId ? 'found it under a plan' : 'found it', when: when(p.createdAt) } : null;
    })(),
    involved: p.stage === 'excluded' ? [] : agents,
    knows: who.knowers(ctx.department),
    facts: [
      ['Way in', way.label],
      ['Kind', p.foundAs === 'supplier' ? 'A supplier' : p.foundAs === 'competitor' ? 'A competitor' : null],
      ['Address', p.hasAddress ? `${p.addressKind === 'role' ? 'A shared address' : 'A person’s address'}, ${p.verification ?? 'unchecked'}` : 'None yet'],
      ['Why off', p.exclusion ? EXCLUSION_WORDS[p.exclusion] : null],
    ],
    next,
    open: { to: `/prospects/${p.id}` },
    ...(p.stage === 'drafted' && p.threadId && ctx.outreach ? { task: { title: `Send the first email to ${name}`, from: `/mail/${p.threadId}` } } : {}),
  };
}

// --- The week's count --------------------------------------------------------

type Figures = WireOutreachDigest['figures'];

export interface DigestReadings {
  firstEmails: string | null;
  drafts: string | null;
  stop: string | null;
  campaigns: string | null;
  templates: string | null;
  mailings: string | null;
  customerio: string | null;
}

const sumOf = (counts: Record<string, number>): number => Object.values(counts).reduce((a, b) => a + b, 0);

function answeredLine(rows: Array<{ name: string; sent: number; replied: number }> | undefined, what: string): string | null {
  const sent = (rows ?? []).filter((r) => r.sent > 0);
  if (sent.length === 0) return null;
  const share = (r: { sent: number; replied: number }) => r.replied / r.sent;
  const ranked = [...sent].sort((a, b) => share(b) - share(a));
  const top = ranked[0]!;
  const bottom = ranked[ranked.length - 1]!;
  const counts = `${sent.reduce((a, r) => a + r.replied, 0)} answered of ${sent.reduce((a, r) => a + r.sent, 0)} sent`;
  if (sent.length < 2 || share(top) === share(bottom)) return `${counts}. One week is a small count, so read it beside the other weeks before you change a ${what}.`;
  return `${counts}. “${top.name}” was answered most often (${top.replied} of ${top.sent}) and “${bottom.name}” least (${bottom.replied} of ${bottom.sent}). One week is a small count, so look at the same ${what} across weeks before you change one.`;
}

/**
 * A line a person can act on for each part of the week, computed from the
 * numbers on the screen and compared with nothing else: no benchmark is
 * assumed, so a share is said as a share and the rest is how to read it.
 */
export function readDigest(f: Figures): DigestReadings {
  const sent = f.firstTouches;
  const decided = sumOf(f.drafts);
  const edited = f.drafts.sent_edited ?? 0;
  const unchanged = f.drafts.sent_unchanged ?? 0;
  const thrown = f.drafts.discarded ?? 0;
  const breached = f.campaignsEnded.breach ?? 0;
  const complaints = f.suppressedByReason.complained ?? 0;
  const bounced = f.suppressedByReason.bounced_hard ?? 0;
  const customerBases = sumOf(f.lifecycle.bases);
  return {
    firstEmails:
      sent === 0
        ? f.replies > 0
          ? `${plural(f.replies, 'reply', 'replies')} came back and no first email went out this week, so these answer earlier ones. Answer them in Mail.`
          : null
        : `${plural(f.replies, 'reply', 'replies')} to ${plural(sent, 'first email')} is ${percent(f.replies, sent)} in every 100. Replies in a week can answer earlier weeks’ emails, so read the share beside the other weeks. ${f.replies > 0 ? 'Answer each reply in Mail; a person does that.' : 'No reply to answer.'}`,
    drafts:
      decided === 0
        ? null
        : `${plural(decided, 'draft')} decided: ${unchanged} sent unchanged, ${edited} sent after editing, ${thrown} thrown away.${unchanged + edited > 0 ? ` Of those sent, ${percent(edited, unchanged + edited)} in every 100 were edited first: the closer to none, the closer the agent’s drafts are to what a person would send.` : ''}`,
    stop:
      f.suppressed === 0
        ? 'Nobody asked us to stop this week. Nothing to do.'
        : `${plural(f.suppressed, 'address', 'addresses')} added to the list${sent > 0 ? `, against ${plural(sent, 'first email')} sent (some of them from earlier emails)` : ''}.${complaints + bounced > 0 ? ` ${complaints > 0 ? `${plural(complaints, 'complaint')}` : ''}${complaints > 0 && bounced > 0 ? ' and ' : ''}${bounced > 0 ? `${plural(bounced, 'address', 'addresses')} that do not exist` : ''} count towards the threshold that ends a campaign; look at the wording and the list the emails came from.` : ' Each is suppressed at once; nothing more to do.'}`,
    campaigns:
      breached > 0
        ? `${plural(breached, 'campaign')} stopped by a threshold agreed in advance. The agent went back to drafts a person sends; a superadmin has the incident with the reading. Draw up a new campaign only once the cause is understood.`
        : sumOf(f.campaignsEnded) > 0
          ? 'A campaign ended by its own terms, not by a threshold. Nothing to do unless more should go.'
          : f.incidents.length > 0
            ? 'An incident was recorded; a superadmin has it. Nothing to do from here.'
            : null,
    templates: answeredLine(f.byTemplate, 'template'),
    mailings: answeredLine(f.byMailing, 'mailing'),
    customerio:
      f.samplesRequested !== undefined && f.samplesRequested > 0
        ? `${plural(f.samplesRequested, 'sample')} requested. Each is someone to send a sample to; a basis recorded is permission to tell Customer.io about them, and nothing more.`
        : customerBases > 0
          ? 'A basis recorded is permission to tell Customer.io about that person, and nothing more. Nothing to do.'
          : null,
  };
}

export function explainDigest(d: WireOutreachDigest, who: Who, ctx: { department: string }): ItemContext {
  const f = d.figures;
  return {
    subject: { kind: 'weekly report', title: `Outreach, ${d.week}` },
    summary: f.quiet
      ? `The platform’s count of the week ${when(f.window.from)} onwards. Nothing went out and nothing came back; the heartbeat was running, and this is its count.`
      : `The platform’s count of the week ${when(f.window.from)} onwards: ${plural(f.firstTouches, 'first email')} sent, ${plural(f.replies, 'reply', 'replies')}, ${plural(f.suppressed, 'address', 'addresses')} added to who said stop.`,
    need: 'Nothing from you to produce it. Read it to see what to do about replies, stops and campaigns.',
    why: 'It is written for your department every Monday, from the records and not from any agent’s account of itself.',
    source: { label: 'Counted by the heartbeat on the Monday after the week ended', when: when(d.createdAt) },
    broughtBy: { name: 'The platform', kind: 'system' },
    knows: who.knowers(ctx.department),
    next: {
      tip: f.quiet
        ? 'Nothing is needed. A quiet week means nothing went out, so check that outreach is meant to be running: a campaign approved, a restaurant with a checked address.'
        : 'Read the line under each part below, then compare with the earlier week using the buttons above.',
    },
  };
}
